/**
 * Sentry Application Performance Monitoring (APM) Configuration
 * 
 * Provides error tracking, performance monitoring, and session replay
 * for the DentalFlow production environment.
 * 
 * Environment variables required:
 * - VITE_SENTRY_DSN: The Sentry Data Source Name (project-specific)
 * - VITE_SENTRY_ENVIRONMENT: 'production' | 'staging' | 'development'
 */
import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || "";
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
const APP_VERSION = import.meta.env.VITE_APP_VERSION || "0.0.0";

/**
 * Initialize Sentry SDK.
 * Only activates in production/staging environments when a DSN is provided.
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    console.info("[Sentry] No DSN configured — skipping initialization.");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: `dentalflow@${APP_VERSION}`,

    // Performance Monitoring
    tracesSampleRate: SENTRY_ENVIRONMENT === "production" ? 0.2 : 1.0,

    // Session Replay (captures user interactions leading to errors)
    replaysSessionSampleRate: 0.01, // 1% of sessions
    replaysOnErrorSampleRate: 1.0,  // 100% of sessions with errors

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,       // HIPAA: mask all text in replays
        blockAllMedia: true,     // HIPAA: block media (X-rays, photos)
      }),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        autoInject: false, // We'll trigger manually from error boundary
      }),
    ],

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "ResizeObserver loop",
      // Network errors (handled by offline detection)
      "Failed to fetch",
      "NetworkError",
      "Load failed",
      // Chunk loading (handled by ChunkLoadErrorHandler)
      "Loading chunk",
      "ChunkLoadError",
    ],

    // Scrub sensitive data before sending to Sentry
    beforeSend(event) {
      // Remove patient PII from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data?.url) {
            // Strip query params that might contain patient data
            try {
              const url = new URL(breadcrumb.data.url);
              url.searchParams.delete("patient_id");
              url.searchParams.delete("phone");
              url.searchParams.delete("email");
              breadcrumb.data.url = url.toString();
            } catch {
              // Not a valid URL, leave as-is
            }
          }
          return breadcrumb;
        });
      }
      return event;
    },

    // Tag events with tenant context for filtering in Sentry dashboard
    beforeSendTransaction(event) {
      return event;
    },
  });

  console.info(`[Sentry] Initialized for ${SENTRY_ENVIRONMENT} (release: dentalflow@${APP_VERSION})`);
}

/**
 * Set user context for Sentry events.
 * Call this after successful authentication.
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  role?: string;
  tenantId?: string;
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
  if (user.role) {
    Sentry.setTag("user.role", user.role);
  }
  if (user.tenantId) {
    Sentry.setTag("tenant.id", user.tenantId);
  }
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Capture a custom error with additional context.
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>
): string {
  return Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a custom message/event.
 */
export function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info"
): void {
  Sentry.captureMessage(message, level);
}

/**
 * Create a performance transaction for custom operations.
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startInactiveSpan({ name, op });
}

// Re-export Sentry's ErrorBoundary for use in React components
export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const SentryProfiler = Sentry.withProfiler;
